import clsx from 'clsx'
import Container from '@objects/container'
import InViewAnimation from '@objects/inViewAnimation'
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core'
import Copy from '@components/copy'
import Button from '@objects/button'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { Contentful } from '@/types/contentfulhelpers'
import { useIntl } from 'react-intl'
import Paragraph from '@objects/paragraph'
import GermanyMap from './components/germanyMap'
import BundeslandSelect from './components/bundeslandSelect'
import Tile from './components/tile'
import TileSkeletonGrid from './components/tileSkeleton'
import useAccidentAtlasData from './hooks/useAccidentAtlasData'
import {
  hasMoreTileRows,
  INITIAL_VISIBLE_ROWS,
  LOAD_MORE_ROW_COUNT,
  partitionAccidentAtlasTileRows,
  sliceTileRows,
} from './lib/partitionTileRows'
import { useAccidentAtlasTileGridStyles } from './lib/tileGridStyles'
import {
  AccidentAtlasBundeslandId,
  AccidentAtlasRegionId,
  DEUTSCHLAND_REGION_ID,
} from './types/region'
import { AccidentAtlasCmsTile } from './types/tile'

export { default as GermanyMap } from './components/germanyMap'
export type { GermanyMapProps } from './components/germanyMap'
export {
  BUNDESLAND_REGION_IDS,
  DEUTSCHLAND_REGION_ID,
  REGION_NAMES,
  getRegionName,
  isAccidentAtlasRegionId,
  isBundeslandRegionId,
  resolveRegionId,
} from './types/region'
export type {
  AccidentAtlasBundeslandId,
  AccidentAtlasRegionId,
} from './types/region'
export type {
  AccidentAtlasChartRenderType,
  AccidentAtlasCmsTile,
  AccidentAtlasTileDisplayKind,
  AccidentAtlasTileView,
  TileProps,
} from './types/tile'
export { toAccidentAtlasCmsTiles } from './types/tile'
export type { AccidentAtlasLoadStatus, AccidentAtlasView } from './types/view'

export type AccidentAtlasProps = RvG.IReactDefaultProps & {
  introSubline?: string
  introCopy?: RvG.Contentful.BasicRichTextType
  backgroundImage?: Contentful.IAsset
  tiles?: Array<AccidentAtlasCmsTile>
}

type StyleProps = {
  dashboardImageUrl?: string
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  introSection: {
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(4),
    [theme.breakpoints.up('lg')]: {
      minHeight: 300,
    },
  },
  introSectionContainer: {
    display: 'flex',
    '& > div': {
      [theme.breakpoints.up('lg')]: {
        flexBasis: '50%',
      },
    },
  },
  introDate: {
    marginBottom: theme.spacing(1),
  },
  mapBand: {},
  mapBandContainer: {
    [theme.breakpoints.down('md')]: {
      padding: 0,
    },
  },
  mapBandInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(4),
    backgroundColor: theme.palette.background.light,
    position: 'relative',
    padding: theme.spacing(12, 4, 12, 4),
    [theme.breakpoints.up('lg')]: {
      borderRadius: 12,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(8),
      padding: theme.spacing(4),
      height: 180,
    },
  },
  mapControls: {
    flex: '0 0 auto',
    [theme.breakpoints.up('lg')]: {
      flex: '0 0 320px',
      alignSelf: 'center',
    },
  },
  map: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      display: 'block',
      flex: '1 1 auto',
      marginLeft: 'auto',
      maxWidth: 420,
      position: 'absolute',
      right: '48px',
      bottom: '-100px',
    },
  },
  dashboardBand: {
    minHeight: 480,
    backgroundColor: theme.palette.background.greyDark,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundImage: ({ dashboardImageUrl }: StyleProps) =>
      dashboardImageUrl ? `url(${dashboardImageUrl})` : undefined,
    [theme.breakpoints.up('md')]: {
      minHeight: 560,
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: '-90px',
    },
  },
  tileSpanRow: {
    gridColumn: '1 / -1',
  },
  tilesFooter: {
    display: 'flex',
    justifyContent: 'center',
    paddingBottom: theme.spacing(8),
    [theme.breakpoints.up('lg')]: {
      paddingBottom: theme.spacing(12),
    },
  },
}))

export default function AccidentAtlas({
  introSubline,
  introCopy,
  backgroundImage,
  tiles,
}: AccidentAtlasProps): ReactElement {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const classes = useStyles({
    dashboardImageUrl: backgroundImage?.fluid?.src,
  })
  const gridClasses = useAccidentAtlasTileGridStyles()
  const intl = useIntl()
  const [selection, setSelection] = useState<
    AccidentAtlasRegionId | undefined
  >()
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS)
  const [showAllCompactTiles, setShowAllCompactTiles] = useState(false)

  const { view, status } = useAccidentAtlasData({ cmsTiles: tiles, selection })
  const isLoading = status === 'idle' || status === 'loading'
  const displayTiles = view?.tiles ?? []
  const desktopTileRows = useMemo(
    () => partitionAccidentAtlasTileRows(displayTiles),
    [displayTiles]
  )
  const visibleDesktopRows = useMemo(
    () => sliceTileRows(desktopTileRows, visibleRowCount),
    [desktopTileRows, visibleRowCount]
  )
  const compactTileLimit = 3
  const visibleCompactTiles = showAllCompactTiles
    ? displayTiles
    : displayTiles.slice(0, compactTileLimit)
  const hasMoreDesktopRows = hasMoreTileRows(desktopTileRows, visibleRowCount)
  const hasMoreCompactTiles = displayTiles.length > compactTileLimit
  const showMoreButton = isDesktop
    ? hasMoreDesktopRows
    : hasMoreCompactTiles && !showAllCompactTiles

  useEffect(() => {
    setVisibleRowCount(INITIAL_VISIBLE_ROWS)
    setShowAllCompactTiles(false)
  }, [selection, displayTiles])

  const selectedBundesland =
    selection && selection !== DEUTSCHLAND_REGION_ID ? selection : undefined

  function handleRegionSelect(next: AccidentAtlasRegionId): void {
    setSelection(next)
  }

  function handleMapSelect(id: AccidentAtlasBundeslandId): void {
    setSelection(id)
  }

  function handleShowMore(): void {
    if (isDesktop) {
      setVisibleRowCount((count) =>
        Math.min(count + LOAD_MORE_ROW_COUNT, desktopTileRows.length)
      )
      return
    }

    setShowAllCompactTiles(true)
  }

  return (
    <section
      className={clsx(classes.root)}
      role="region"
      aria-label={intl.formatMessage({
        id: 'accidentAtlas.section.aria.label',
      })}
    >
      <div className={classes.introSection}>
        <Container type="nomargin" className={classes.introSectionContainer}>
          <InViewAnimation
            group={[
              introSubline && (
                <Paragraph bold className={classes.introDate}>
                  {introSubline}
                </Paragraph>
              ),
              introCopy && <Copy richtext={introCopy} />,
            ]}
          />
        </Container>
      </div>

      <div className={classes.mapBand}>
        <Container type="nomargin" className={classes.mapBandContainer}>
          <InViewAnimation>
            <div className={classes.mapBandInner}>
              <div className={classes.mapControls}>
                <BundeslandSelect
                  value={selection}
                  onChange={handleRegionSelect}
                />
              </div>
              <GermanyMap
                className={classes.map}
                selectedId={selectedBundesland}
                onSelect={handleMapSelect}
              />
            </div>
          </InViewAnimation>
        </Container>
      </div>

      <div
        className={classes.dashboardBand}
        aria-label={intl.formatMessage({
          id: 'accidentAtlas.tilesgroup.aria.label',
        })}
      >
        {(isLoading || displayTiles.length > 0) && (
          <Container type="nomargin">
            {isLoading ? (
              <TileSkeletonGrid
                isDesktop={isDesktop}
                ariaLabel={intl.formatMessage({
                  id: 'accidentAtlas.tilesgroup.aria.label',
                })}
              />
            ) : isDesktop ? (
              <div className={gridClasses.tilesRows}>
                {visibleDesktopRows.map((row, rowIndex) => (
                  <div
                    key={`tile-row-${rowIndex}`}
                    className={gridClasses.tilesRow}
                    style={
                      {
                        '--tile-columns': row.columns,
                      } as React.CSSProperties
                    }
                  >
                    {row.tiles.map((tile) => (
                      <Tile
                        key={tile.tileId}
                        {...tile}
                        compactChartLayout={
                          !(row.columns === 2 && row.tiles.length === 1)
                        }
                        className={
                          row.columns === 2 && row.tiles.length === 1
                            ? classes.tileSpanRow
                            : undefined
                        }
                      />
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className={gridClasses.tilesGrid}>
                {visibleCompactTiles.map((tile) => (
                  <Tile key={tile.tileId} {...tile} compactChartLayout />
                ))}
              </div>
            )}
            {!isLoading && showMoreButton && (
              <div className={classes.tilesFooter}>
                <Button
                  type="tertiary"
                  onClick={handleShowMore}
                  aria-label={intl.formatMessage({
                    id: 'accidentAtlas.more.aria.label',
                  })}
                >
                  {intl.formatMessage({ id: 'accidentAtlas.more.button' })}
                </Button>
              </div>
            )}
          </Container>
        )}
      </div>
    </section>
  )
}
